/**
 * PatientVideoCallPage - Dedicated Video Call Page for Patients
 * 
 * This page provides a full-screen video calling interface for patients
 * to join telehealth appointments with doctors and clinicians.
 * 
 * UPDATED: Now uses VisionFlex instead of Amazon Chime SDK
 * 
 * Author: AI Assistant
 * Date: 2025-10-14
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, User, MapPin, FileText } from 'lucide-react';
// Temporary: Use new window mode until VisionFlex whitelists https://app.medifusionpro.io/
// Switch back to VisionFlexPlayer once domain is whitelisted by VisionFlex
import VisionFlexPlayer from '../../../components/VideoCall/VisionFlexPlayer';
import { authService } from '../../auth/services/authService';
import { patientService } from '../services/patientService';
import { validateBookingAccessWithNavigation } from '../../../services/videoCallService';
import toast from 'react-hot-toast';

const PatientVideoCallPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { booking_id } = useParams(); // Get booking ID from URL
  const appointmentData = location.state?.appointmentData; // Get appointment data from navigation state
  const currentUser = authService.getCurrentUser();
  
  // State for call management
  const [callStarted, setCallStarted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [showPreJoinScreen, setShowPreJoinScreen] = useState(true);
  const [validatedAppointmentData, setValidatedAppointmentData] = useState(null);
  const [loadingValidation, setLoadingValidation] = useState(true);
  const [validationError, setValidationError] = useState(null);
  
  // Validate booking access and get appointment data
  const validateBookingAccess = async () => {
    // If appointment data is provided via state, validate it matches the booking ID
    if (appointmentData && appointmentData.booking_id === parseInt(booking_id)) {
      setValidatedAppointmentData(appointmentData);
      setLoadingValidation(false);
      return;
    }
    
    // Use the video call service for comprehensive validation
    await validateBookingAccessWithNavigation(
      booking_id,
      navigate,
      (error) => {
        setValidationError(error);
        toast.error(error);
      },
      setLoadingValidation,
      (result) => {
        // Success callback - set validated appointment data
        let validatedData;
        
        if (result.bookingInfo) {
          // Use data from backend
          validatedData = {
            booking_id: parseInt(booking_id),
            ...result.bookingInfo,
            id: parseInt(booking_id) // Ensure id field exists for compatibility
          };
        } else if (appointmentData) {
          // Fallback to state data
          validatedData = appointmentData;
        } else {
          setValidationError('No appointment data available');
          toast.error('No appointment data available');
          setTimeout(() => navigate('/patient/dashboard'), 3000);
          return;
        }
        
        setValidatedAppointmentData(validatedData);
        console.log('✅ Booking access validated for patient:', currentUser.id, 'booking:', booking_id);
      }
    );
  };

  // Get appointment type display name
  const getAppointmentTypeDisplayName = () => {
    const data = validatedAppointmentData || appointmentData;
    if (!data?.appointment_type) return 'Video Consultation';
    
    const type = data.appointment_type.toLowerCase();
    if (type === 'general_telehealth' || type === 'general telehealth') {
      return 'General Telehealth Consultation';
    } else if (type === 'clinical_telehealth' || type === 'clinical telehealth') {
      return 'Clinical Telehealth Consultation';
    }
    return 'Video Consultation';
  };
  
  // Validate booking access on component mount
  useEffect(() => {
    validateBookingAccess();
  }, [booking_id, currentUser.id]);

  // Set page title
  useEffect(() => {
    document.title = `MediFusion - ${getAppointmentTypeDisplayName()}`;
  }, [validatedAppointmentData]);
  
  // Handle call end
  const handleCallEnd = () => {
    setCallEnded(true);
    setCallStarted(false);
    
    // Dismiss any existing toasts to prevent overlap
    toast.dismiss();
    
    // Show brief success message
    toast.success('Call ended', { duration: 1000 });
    
    // Redirect to home immediately (no need for long delay)
    setTimeout(() => {
      navigate('/patient/dashboard');
    }, 1000);
  };
  
  // Handle join call
  const handleJoinCall = () => {
    setShowPreJoinScreen(false);
    setCallStarted(true);
  };
  
  // Handle back navigation
  const handleGoBack = () => {
    if (callStarted) {
      const confirmLeave = window.confirm('Are you sure you want to leave the video call?');
      if (confirmLeave) {
        navigate('/patient/dashboard');
      }
    } else {
      navigate('/patient/dashboard');
    }
  };
  
  // Format appointment time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      return new Date(`1970-01-01T${timeString}`).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timeString;
    }
  };
  
  // Format appointment date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString([], {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };
  
  // Show loading while validating booking access
  if (loadingValidation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating appointment access...</p>
        </div>
      </div>
    );
  }

  // Show error if validation failed
  if (validationError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6m0 0l6 6m-6-6l6-6"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{validationError}</p>
          <p className="text-sm text-gray-500 mb-4">Redirecting to your home page...</p>
          <button
            onClick={() => navigate('/patient/dashboard')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Show loading if no validated appointment data yet
  if (!validatedAppointmentData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading appointment details...</p>
        </div>
      </div>
    );
  }
  
  // Show call ended screen
  if (callEnded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white p-8 rounded-lg shadow-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Ended</h2>
          <p className="text-gray-600 mb-6">Thank you for your video consultation.</p>
          <p className="text-sm text-gray-500 mb-4">Redirecting to your home page...</p>
          <button
            onClick={() => navigate('/patient/dashboard')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Show pre-join screen
  if (showPreJoinScreen) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Similar to Doctor's Page */}
        <div className="bg-white shadow-sm mb-6">
          {/* Top Header - Logos and User */}
          <div className="flex justify-between items-center px-6 py-1">
            {/* Left side - MediFusion Logo */}
            <div className="flex items-center">
              <img
                src="/login_medi_logo.png"
                alt="MediFusion"
                className="h-8 w-auto"
              />
            </div>
            
            {/* User Name and GARLI MEDI TECH Logo - Right */}
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 mr-10">
                <User className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">
                  {currentUser?.first_name} {currentUser?.last_name}
                </span>
              </div>
              <img
                src="/garlimeditech.png"
                alt="GARLI MEDI TECH"
                className="h-6 w-auto"
              />
            </div>
          </div>
          
          {/* Blue Header Block with Appointment Type */}
          <div className="bg-blue-600 text-white px-6 py-1.5">
            <div className="flex items-center justify-between">
              <button
                onClick={handleGoBack}
                className="inline-flex items-center px-3 py-1.5 text-sm text-white hover:text-gray-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Home
              </button>
              <h1 className="text-lg font-semibold">{getAppointmentTypeDisplayName()}</h1>
              <div className="w-24"></div> {/* Spacer for centering */}
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Appointment Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Appointment Details</h2>
              
              <div className="space-y-4">
                {/* Doctor Information */}
                <div className="flex items-start space-x-3">
                  <User className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Healthcare Provider</p>
                    <p className="text-gray-600">
                      {validatedAppointmentData.doctor_name}
                      {validatedAppointmentData.appointment_type === 'clinical_telehealth' && 
                        validatedAppointmentData.clinician_name && 
                        ` • ${validatedAppointmentData.clinician_name}`
                      }
                    </p>
                  </div>
                </div>
                
                {/* Date & Time */}
                <div className="flex items-start space-x-3">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Date & Time</p>
                    <p className="text-gray-600">
                      {formatDate(validatedAppointmentData.appointment_date)}
                    </p>
                    <p className="text-gray-600">
                      {formatTime(validatedAppointmentData.appointment_time)}
                    </p>
                  </div>
                </div>
                
                {/* Location */}
                {validatedAppointmentData.clinic_location && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Clinic</p>
                      <p className="text-gray-600">{validatedAppointmentData.clinic_location}</p>
                    </div>
                  </div>
                )}
                
                {/* Reason */}
                {validatedAppointmentData.reason && (
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Reason for Visit</p>
                      <p className="text-gray-600">{validatedAppointmentData.reason}</p>
                    </div>
                  </div>
                )}
                
                {/* Appointment Type */}
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 bg-blue-600 rounded-full mt-0.5 flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Appointment Type</p>
                    <p className="text-gray-600 capitalize">
                      {validatedAppointmentData.appointment_type?.replace('_', ' ') || 'Telehealth'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Pre-join Instructions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Before You Join</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-medium text-blue-600">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Check Your Camera & Microphone</p>
                    <p className="text-sm text-gray-600">Make sure your camera and microphone are working properly</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-medium text-blue-600">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Find a Quiet Space</p>
                    <p className="text-sm text-gray-600">Choose a quiet, well-lit location for your consultation</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-medium text-blue-600">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Have Your Information Ready</p>
                    <p className="text-sm text-gray-600">Prepare any questions or symptoms you want to discuss</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-xs font-medium text-blue-600">4</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Stable Internet Connection</p>
                    <p className="text-sm text-gray-600">Ensure you have a reliable internet connection</p>
                  </div>
                </div>
              </div>
              
              {/* Join Button */}
              <div className="mt-8">
                <button
                  onClick={handleJoinCall}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                  </svg>
                  <span>Join Video Call</span>
                </button>
                
                <p className="text-xs text-gray-500 text-center mt-2">
                  By joining, you agree to the consultation terms and privacy policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show video call interface
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Similar to Doctor's Page */}
      <div className="bg-white shadow-sm">
        {/* Top Header - Logos and User */}
        <div className="flex justify-between items-center px-6 py-1">
          {/* Left side - MediFusion Logo */}
          <div className="flex items-center">
            <img
              src="/login_medi_logo.png"
              alt="MediFusion"
              className="h-8 w-auto"
            />
          </div>
          
          {/* User Name and GARLI MEDI TECH Logo - Right */}
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2 mr-10">
              <User className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                {currentUser?.first_name} {currentUser?.last_name}
              </span>
            </div>
            <img
              src="/garlimeditech.png"
              alt="GARLI MEDI TECH"
              className="h-6 w-auto"
            />
          </div>
        </div>
        
        {/* Blue Header Block with Appointment Type and Controls */}
        <div className="bg-blue-600 text-white px-6 py-1.5">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="inline-flex items-center px-3 py-1.5 text-sm text-white hover:text-gray-200 transition-colors bg-red-500 hover:bg-red-600 rounded-md"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Leave Call
            </button>
            
            <div className="text-center">
              <h1 className="text-base font-medium">{getAppointmentTypeDisplayName()}</h1>
              <p className="text-sm text-blue-100">
                {validatedAppointmentData.doctor_name}
                {validatedAppointmentData.appointment_type === 'clinical_telehealth' && 
                  validatedAppointmentData.clinician_name && 
                  ` • ${validatedAppointmentData.clinician_name}`
                }
              </p>
            </div>
            
            <div className="text-right text-sm">
              <p>{formatDate(validatedAppointmentData.appointment_date)}</p>
              <p className="text-blue-200">{formatTime(validatedAppointmentData.appointment_time)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* VisionFlex Video Call Component */}
      <div className="flex-1" style={{ height: 'calc(100vh - 95px)' }}>
        <VisionFlexPlayer
          bookingId={validatedAppointmentData.booking_id}
          userRole="patient"
          appointmentData={validatedAppointmentData}
          onCallEnd={handleCallEnd}
          className="w-full h-full"
        />
      </div>
    </div>
  );
};

export default PatientVideoCallPage;

